import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { Button, Input, notification, Select, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import {
  DownloadXmlInvoiceAction,
  DownloadXmlInvoiceRequest,
  DownloadXmlInvoiceResponseMessage,
} from '@/core/domain/invoice/invoice/Invoice.types';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import { useInvoiceDocumentDownload } from '@/core/domain/invoice/invoice/invoice.hooks';
import { InvoiceSituation } from '@/core/domain/invoice/invoices/invoices.enums';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import useInvoiceFunctions from '@/core/domain/invoiceTransmissionHub/Invoice/Invoice.functions';
import {
  getInvoiceDocumentOptions,
  InvoiceDocument,
  InvoiceDocumentTranslation,
  InvoiceDocumentTypeMapper,
} from './InvoiceDocument.constants';
import { useCompanyMutation } from '@/core/domain/enterprise/Companies/Companies.hooks';

interface Props {
  isApiConditionMet: boolean;
}

const InvoiceDocumentOptionsButton = ({ isApiConditionMet }: Props) => {
  const {
    invoiceId,
    invoice: { data: invoice },
    isNfe,
    isNfc,
    isNfs,
    useInternalInvoiceApiNfe,
    useInternalInvoiceApiNfc,
    useInternalInvoiceApiNfs,
    handleSubmit,
  } = useInvoiceFormContext();

  const invoiceNotDeleted = !invoice?.deleted;

  const [invoiceDocumentType, setInvoiceDocument] = useState<InvoiceDocument>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const report = useReportGenerate();

  const { data: company, mutateAsync: getCompany } = useCompanyMutation();

  const invoiceKey = invoice?.key;
  const invoiceNumber = invoice?.number;

  const isNfeOrInternalApiNfe = isNfe && useInternalInvoiceApiNfe;
  const isNfcOrInternalApiNfc = isNfc && useInternalInvoiceApiNfc;
  const isNfsOrInternalApiNfs = isNfs && useInternalInvoiceApiNfs;

  const downloadXml = useInvoiceDocumentDownload();

  useEffect(() => {
    if (invoice?.companyId) {
      getCompany(invoice?.companyId);
    }
  }, [invoice?.companyId]);

  const isBhSpCity = [310, 57].includes(
    invoice?.cityId || company?.companyAddress?.cityId || 0,
  );

  const handleDisabledMirrorXmlPdf = useMemo(() => {
    if (!invoice?.situation) return true;

    const allowedSituations = [
      InvoiceSituation.IN_TYPING,
      InvoiceSituation.VALIDATED,
      InvoiceSituation.PROCESSING,
    ];

    const showMirrorPdfXml = allowedSituations.includes(invoice?.situation);

    if (
      isNfeOrInternalApiNfe ||
      isNfcOrInternalApiNfc ||
      isNfsOrInternalApiNfs
    ) {
      return !showMirrorPdfXml;
    }

    return true;
  }, [
    invoice?.situation,
    isNfeOrInternalApiNfe,
    isNfcOrInternalApiNfc,
    isNfsOrInternalApiNfs,
  ]);

  const handleDisabledXmlPdf = useMemo(() => {
    if (!invoice?.situation) return true;

    const allowedSituations = [
      InvoiceSituation.AUTHORIZED,
      InvoiceSituation.CANCELED,
      InvoiceSituation.PROCESSING_CANCELLATION,
      InvoiceSituation.DENIED,
    ];

    const showPdfXml = allowedSituations.includes(invoice?.situation);

    if (
      isNfeOrInternalApiNfe ||
      isNfcOrInternalApiNfc ||
      isNfsOrInternalApiNfs
    ) {
      return !showPdfXml;
    }

    if (!isNfeOrInternalApiNfe && !isNfcOrInternalApiNfc) {
      return !invoice?.xmlPath && !invoice?.plugNotasId && invoiceNotDeleted;
    }

    return true;
  }, [
    invoice?.situation,
    isNfeOrInternalApiNfe,
    isNfcOrInternalApiNfc,
    isNfsOrInternalApiNfs,
    invoice?.xmlPath,
    invoice?.plugNotasId,
    invoiceNotDeleted,
  ]);

  const options = getInvoiceDocumentOptions({
    isNfs,
    isBhSpCity,
    handleDisabledMirrorXmlPdf,
    handleDisabledXmlPdf,
    invoiceSituation: invoice?.situation,
  });

  useEffect(() => {
    if (invoiceDocumentType) return;

    const optionsEnabled = options.find(({ disabled }) => !disabled);

    if (invoice?.situation === InvoiceSituation.AUTHORIZED) {
      setInvoiceDocument(InvoiceDocument.pdf);
    } else if (optionsEnabled) {
      setInvoiceDocument(optionsEnabled.value);
    }
  }, [invoiceDocumentType, options, invoice?.situation]);

  const {
    handleInvoicePdf,
    handleInvoiceXml,
    handleMirrorInvoiceXml,
    handleMirrorInvoicePdf,
    handleInvoiceRpsPdf,
    handleInvoiceUploadDocument,
    allIsLoadingInternal,
  } = useInvoiceFunctions();

  const createFileName = () => {
    if (isNfe) return invoiceKey || '';
    if (isNfc) return invoiceKey || '';
    if (isNfs) return invoiceNumber || '';
    return '';
  };

  const handleGenerateReport = async (
    format: FileType,
    type: string,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      if (invoiceDocumentType === InvoiceDocument.xmlMirror) {
        const result = await handleMirrorInvoiceXml(isBhSpCity);

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      }

      if (invoiceDocumentType === InvoiceDocument.pdfMirror) {
        const result = await handleMirrorInvoicePdf(isBhSpCity);

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      }

      if (invoiceDocumentType === InvoiceDocument.xml) {
        const obj: DownloadXmlInvoiceRequest = {
          invoiceId: Number(invoiceId),
          docType: 0,
          action:
            type === 'download'
              ? DownloadXmlInvoiceAction.Download
              : DownloadXmlInvoiceAction.Display,
        };

        const result = isApiConditionMet
          ? await handleInvoiceXml()
          : await downloadXml.mutateAsync(obj);

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      }

      if (invoiceDocumentType === InvoiceDocument.pdf) {
        const obj: DownloadXmlInvoiceRequest = {
          invoiceId: Number(invoiceId),
          docType: 1,
        };

        const result = isApiConditionMet
          ? await handleInvoicePdf()
          : await downloadXml.mutateAsync(obj);

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      } else if (invoiceDocumentType === InvoiceDocument.pdfRps) {
        const result = await handleInvoiceRpsPdf();

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);
        if (callback && !response?.message) {
          callback(result);
        }
      }

      if (invoiceDocumentType === InvoiceDocument.pdfRps) {
        const result = await handleInvoiceRpsPdf();

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      }

      if (invoiceDocumentType === InvoiceDocument.uploadFile) {
        const result = await handleInvoiceUploadDocument();

        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

        if (callback && !response?.message) {
          callback(result);
        }
      }
    } catch {}
    setFileUnloading(format);
  };

  const { xml, xmlMirror, uploadFile } = InvoiceDocument;

  const isXML =
    invoiceDocumentType === xml || invoiceDocumentType === xmlMirror;
  const isTxt = invoiceDocumentType === uploadFile;

  const fileExtension = isXML ? 'xml' : isTxt ? 'txt' : 'pdf';

  const fileType = isXML ? 'application/xml' : 'application/pdf';

  const fileName = `${
    invoiceDocumentType ? InvoiceDocumentTranslation[invoiceDocumentType] : ''
  }-${createFileName()}.${fileExtension}`;

  const allIsLoading = allIsLoadingInternal || downloadXml.isLoading;

  const saveWhen = [
    InvoiceSituation.VALIDATED,
    InvoiceSituation.IN_TYPING,
    InvoiceSituation.INTERRUPTED,
  ];

  const handleGenerate = (type: string) => {
    const documentType = invoiceDocumentType
      ? InvoiceDocumentTypeMapper[invoiceDocumentType]
      : 'PDF';

    handleGenerateReport(documentType as FileType, type, (result) => {
      const contentTypeZip = (result as Blob).type === 'application/zip';

      const adjustedFileName = contentTypeZip
        ? `${fileName}.zip`
        : documentType === 'TXT'
        ? `${fileName}.txt`
        : fileName;

      if (type === 'download') {
        report.downloadFile({
          result,
          fileName: adjustedFileName,
        });
      }

      if (type === 'view') {
        report.openFile({
          result,
          fileName: adjustedFileName,
          fileType: contentTypeZip ? 'application/zip' : fileType,
        });
      }

      if (type === 'view' && contentTypeZip) {
        notification.info({
          message: 'Nota Fiscal com Múltiplos Anexos',
          description: (
            <>
              <Typography.Text>
                Esta nota fiscal contém vários arquivos XML agrupados em um
                pacote compactado (ZIP). Para visualizar o conteúdo:
              </Typography.Text>

              <ul>
                <li>O download do arquivo ZIP será iniciado automaticamente</li>
                <li>Extraia os arquivos no seu dispositivo</li>
                <li>Abra cada XML individualmente para análise</li>
              </ul>
            </>
          ),

          duration: 6,
        });
      }
    });
  };

  const handleViewReport = async () => {
    if (invoice?.situation && saveWhen.includes(invoice?.situation)) {
      handleSubmit().then(() => {
        handleGenerate('view');
      });
    } else {
      handleGenerate('view');
    }
  };

  const handleDownloadReport = async () => {
    if (invoice?.situation && saveWhen.includes(invoice.situation)) {
      handleSubmit().then(() => {
        handleGenerate('download');
      });
    } else {
      handleGenerate('download');
    }
  };

  return (
    <>
      {allIsLoading && (
        <ModalLoadingOverlay messages={['Gerando documento da nota...']} />
      )}

      <Input.Group
        compact
        style={{
          flexWrap: 'nowrap',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Select
          options={options}
          value={invoiceDocumentType}
          onChange={setInvoiceDocument}
          style={{ width: '100%', overflow: 'hidden' }}
          disabled={options.every(({ disabled }) => disabled)}
          placeholder="Opções do Documento"
          placement="topLeft"
        />

        <Button
          type="primary"
          icon={<DownloadOutlined />}
          key="PDF"
          onClick={handleDownloadReport}
          loading={fileLoading.PDF}
          disabled={
            !invoiceDocumentType ||
            !options.find((a) => a.value === invoiceDocumentType)
              ?.downloadButton
          }
          style={{ minWidth: '30px' }}
        />

        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={handleViewReport}
          key="viewReport"
          loading={fileLoading.REPORT_VIEW}
          disabled={
            !invoiceDocumentType ||
            !options.find((a) => a.value === invoiceDocumentType)?.viewButton
          }
          style={{ minWidth: '30px' }}
        />
      </Input.Group>
    </>
  );
};

export default InvoiceDocumentOptionsButton;
